import { forwardRef } from 'react';

import {
  InputBaseProps,
  IconButton,
  InputLabel,
  Tooltip,
  useTheme,
} from '@mui/material';

import { IcGPS } from '@/assets/images';
import { IAddressForm } from '@/models/address';

import { AddressItem } from './AddressItem';
import { AddressOptionType } from './AddressSearch';
import { MunicipalityTypeItem } from './MunicipalityTypeItem';
import { AddressFieldRoot } from './styles';
import { SubAddressItem } from './SubAddressItem';
import { BaseFieldProps } from '../../FormElements';

export interface AddressFormProps
  extends Omit<InputBaseProps, 'value' | 'error' | 'onChange'>,
    BaseFieldProps {
  value?: IAddressForm | null;
  locationRestriction?: google.maps.LatLngBoundsLiteral;
  locationBias?: string | google.maps.LatLngLiteral | google.maps.CircleLiteral;
  overrideUpdateAddress?: (newAddress?: IAddressForm | null) => void;
  pendingAddress?: IAddressForm | null;
  onChange: (newAddress?: IAddressForm | null) => void;
  disableClearable?: boolean;
  disabled?: boolean;
  showMileMarkerOption?: boolean;
  showGpsIcon?: boolean;
  showMunicipalityTypeInput?: boolean;
  showSubAddress?: boolean;
  addressColumnsTemplate?: string;
  showTruncatedTooltip?: boolean;
  enableBusinessName?: boolean;
  onAddressSelectionStart?: (previewAddress: IAddressForm) => void;
  onAddressSelectionError?: () => void;
}

function openInGoogleMaps(address: string) {
  const encodedAddress = encodeURIComponent(address);
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
    '_blank',
  );
}

const getAddressString = (addressData?: IAddressForm | null): string => {
  if (!addressData) return '';
  const parts = [
    addressData.addressNumber,
    addressData.completeStreetName,
    addressData.subAddress,
    addressData.placeName,
    addressData.state,
    addressData.zipcode,
    addressData.country,
  ].filter(Boolean);

  return parts.join(', ');
};

export const AddressForm = forwardRef<HTMLDivElement, AddressFormProps>(
  (
    {
      name,
      value,
      onChange,
      disabled,
      label,
      required,
      showGpsIcon: showGpsIconProp,
      showMunicipalityTypeInput,
      showSubAddress = true,
      addressColumnsTemplate,
      showTruncatedTooltip,
      enableBusinessName,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const updateCurrentAddress = (newAddress?: IAddressForm | null) => {
      if (disabled) return;
      onChange(newAddress);
    };

    const handleMapsClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const fullAddress = getAddressString(value);
      if (fullAddress) {
        openInGoogleMaps(fullAddress);
      }
    };

    const hasAddress = getAddressString(value).length > 0;
    // default: show the GPS/marker icon unless explicitly disabled.
    const showGpsIcon = showGpsIconProp ?? true;

    const gpsIconButton =
      hasAddress && showGpsIcon ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Spacer to align the icon with inputs that have an InputLabel */}
          <InputLabel sx={{ mt: 1, visibility: 'hidden' }} aria-hidden>
            &nbsp;
          </InputLabel>
          <Tooltip title="Open in Google Maps" placement="top">
            <IconButton
              onClick={handleMapsClick}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '6px',
                border: `1px solid ${
                  theme.palette.mode === 'dark' ? '#566F7D' : '#CFD6DA'
                }`,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.background.formSubHeader
                    : '#FFFFFF',
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.text.primary
                    : '#3E5A6A',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark' ? '#223640' : '#F5F7F7',
                },
              }}
            >
              <IcGPS />
            </IconButton>
          </Tooltip>
        </div>
      ) : null;

    return (
      <AddressFieldRoot ref={ref}>
        <div
          style={{
            display: 'grid',
            gap: '8px',
            alignItems: 'flex-start',
            width: '100%',
            gridTemplateColumns: showMunicipalityTypeInput
              ? 'repeat(3, minmax(0, 1fr))'
              : !showSubAddress
              ? addressColumnsTemplate ?? 'minmax(0, 1fr)'
              : addressColumnsTemplate ?? 'minmax(0, 1fr) minmax(0, 1fr) auto',
          }}
        >
          <div style={{ width: '100%' }}>
            <AddressItem
              updateCurrentAddress={(newAddress) => {
                // To avoid duplicate event propagation.
                // Sometimes the child component can register a change even
                // when no change occurred.
                if (value !== newAddress) {
                  updateCurrentAddress(newAddress);
                }
              }}
              overrideUpdateAddress={rest.overrideUpdateAddress}
              name={name}
              value={(value ?? null) as AddressOptionType | null}
              disabled={disabled}
              label={label}
              required={required}
              showTruncatedTooltip={showTruncatedTooltip}
              enableBusinessName={enableBusinessName}
              onAddressSelectionStart={rest.onAddressSelectionStart}
              onAddressSelectionError={rest.onAddressSelectionError}
              {...rest}
            />
          </div>

          {showSubAddress && (
            <div style={{ width: '100%' }}>
              <SubAddressItem
                updateCurrentAddress={updateCurrentAddress}
                name={name}
                value={(value ?? null) as AddressOptionType | null}
                disabled={disabled}
                showTruncatedTooltip={showTruncatedTooltip}
                enableBusinessName={enableBusinessName}
              />
            </div>
          )}

          {showMunicipalityTypeInput && (
            <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              <div style={{ width: '100%', minWidth: 0 }}>
                <MunicipalityTypeItem
                  value={value || undefined}
                  updateCurrentAddress={updateCurrentAddress}
                  disabled={disabled}
                  clearable={true}
                />
              </div>

              {gpsIconButton}
            </div>
          )}

          {!showMunicipalityTypeInput && gpsIconButton}
        </div>
      </AddressFieldRoot>
    );
  },
);

AddressForm.displayName = 'custom address form';
